import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getTechniciens, getCRList, getStats, getImportStatsToday, getSuiviCRIStats, getDashboardAlertes } from '../services/api';
import { useSettings } from '../App';
import api from '../services/api';

const ACTION_LABEL = { validated: 'Validé', rejected: 'Rejeté' };
const ACTION_COLOR = { validated: 'var(--green2)', rejected: '#ff6b6b' };

/**
 * Le numéro au format international sans « + », celui qu'attend wa.me.
 *
 * Les mêmes règles que l'envoi WhatsApp du serveur, pour que le lien de la
 * page et le message automatique joignent bien la même personne : les espaces
 * sautent, le « + » saute, et un 0 en tête devient 33.
 */
function numeroInternational(tel) {
  const brut = String(tel || '').replace(/[\s.()-]/g, '').replace(/^\+/, '');
  if (!/^\d{9,15}$/.test(brut)) return null;
  if (brut.startsWith('0') && brut.length === 10) return '33' + brut.slice(1);
  return brut;
}

/** Le numéro tel qu'on le lit : « 06 12 34 56 78 », ou l'international groupé. */
function numeroLisible(tel) {
  const n = numeroInternational(tel);
  if (!n) return String(tel || '');
  const national = n.startsWith('33') ? '0' + n.slice(2) : n;
  return national.length === 10
    ? national.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
    : '+' + n;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const widgets = settings?.dashboard?.widgets || {};

  const [techs, setTechs] = useState([]);
  // La vignette ouverte, une seule à la fois : deux fiches de contact
  // dépliées côte à côte ne servent à rien et font sauter la grille.
  const [techOuvert, setTechOuvert] = useState(null);
  const [crPending, setCRPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [importStats, setImportStats] = useState(null);
  const [criStats, setCriStats] = useState(null);
  const [alertes, setAlertes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState(Date.now());
  const [newCRAlert, setNewCRAlert] = useState(false);
  const [waStatus, setWaStatus] = useState(null);
  const [interJour, setInterJour] = useState(null);
  const [loadingInterJour, setLoadingInterJour] = useState(true);

  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [techData, crData, statsData, actData, importData, criData, alertesData] = await Promise.all([
        getTechniciens().catch(() => ({ techniciens: [] })),
        getCRList({ status: 'pending' }).catch(() => ({ crs: [] })),
        getStats('month').catch(() => null),
        fetch('/api/admin/activity', { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }).then(r => r.json()).catch(() => ({ activity: [] })),
        getImportStatsToday().catch(() => null),
        getSuiviCRIStats().catch(() => null),
        getDashboardAlertes().catch(() => null),
      ]);
      const pending = crData.crs || [];
      if (silent && pending.length > crPending.length) setNewCRAlert(true);
      setTechs(techData.techniciens || []);
      setCRPending(pending);
      setStats(statsData);
      setActivity(actData.activity || []);
      if (importData) setImportStats(importData);
      if (criData) setCriStats(criData);
      if (alertesData) setAlertes(alertesData);
      setLastCheck(Date.now());
    } finally {
      if (!silent) setLoading(false);
    }
  }, [crPending.length]);

  const loadInterJour = useCallback(() => {
    setLoadingInterJour(true);
    api.get('/admin/dashboard/inter-jour')
      .then(r => setInterJour(r.data))
      .catch(() => setInterJour({ interventions: [], total: 0 }))
      .finally(() => setLoadingInterJour(false));
  }, []);

  useEffect(() => { load(); loadInterJour(); }, []);

  // Auto-refresh toutes les 30 secondes
  useEffect(() => {
    const interval = setInterval(() => load(true), 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Polling statut WhatsApp toutes les 60 secondes
  useEffect(() => {
    const checkWA = () =>
      fetch('/api/admin/whatsapp/status', { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
        .then(r => r.json()).then(d => setWaStatus(d.status)).catch(() => {});
    checkWA();
    const interval = setInterval(checkWA, 60000);
    return () => clearInterval(interval);
  }, []);

  const kpis = [
    { val: loading ? '…' : techs.filter(t => !t.disabled).length, lbl: 'Techniciens actifs', color: 'var(--blue2)' },
    { val: loading ? '…' : crPending.length, lbl: 'CR en attente', color: 'var(--amber2)' },
    { val: loading ? '…' : (stats?.totaux?.cleared ?? '—'), lbl: 'CLEARED ce mois', color: 'var(--green2)' },
    { val: loading ? '…' : (stats?.totaux?.total ?? '—'), lbl: 'Interventions ce mois', color: 'var(--text)' },
  ];

  // Determine if widgets should show (default true if settings not loaded yet)
  const show = (key) => settings === null || widgets[key] !== false;

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Tableau de bord</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, textTransform: 'capitalize' }}>{dateStr}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {newCRAlert && (
            <div onClick={() => { setNewCRAlert(false); navigate('/cr?filter=pending'); }}
              style={{ background: 'var(--amber2)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', animation: 'pulse 1s infinite' }}>
              🔔 Nouveau CR !
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            Actualisé à {new Date(lastCheck).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>

      <div className="page">

        {/* ── Alerte WhatsApp déconnecté ── */}
        {(waStatus === 'qr_pending' || waStatus === 'disconnected') && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            background: waStatus === 'qr_pending' ? '#FEF3C7' : '#FEE2E2',
            border: `2px solid ${waStatus === 'qr_pending' ? '#F59E0B' : '#DC2626'}`,
            borderRadius: 12, padding: '14px 20px', marginBottom: 20,
            animation: 'pulse 2s infinite',
          }}>
            <div style={{ fontSize: 28, flexShrink: 0 }}>
              {waStatus === 'qr_pending' ? '📱' : '⚠️'}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: waStatus === 'qr_pending' ? '#92400E' : '#991B1B', marginBottom: 2 }}>
                {waStatus === 'qr_pending' ? 'WhatsApp en attente de scan QR Code' : 'WhatsApp déconnecté'}
              </div>
              <div style={{ fontSize: 13, color: waStatus === 'qr_pending' ? '#78350F' : '#7F1D1D' }}>
                {waStatus === 'qr_pending'
                  ? 'Un QR Code est disponible — scannez-le depuis WhatsApp pour rétablir la connexion. Les envois automatiques sont suspendus.'
                  : 'WhatsApp n\'est plus connecté. Les messages automatiques (rappels, rapports) ne sont plus envoyés.'}
              </div>
            </div>
            <button
              onClick={() => navigate('/sms')}
              style={{
                background: waStatus === 'qr_pending' ? '#F59E0B' : '#DC2626',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '10px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer', flexShrink: 0,
              }}>
              {waStatus === 'qr_pending' ? 'Scanner le QR Code →' : 'Reconnecter →'}
            </button>
          </div>
        )}

        {/* KPIs */}
        {show('kpis') && (
          <div className="stats-grid">
            {kpis.map((s, i) => (
              <div key={i} className="stat-card">
                <div className="val" style={{ color: s.color }}>{s.val}</div>
                <div className="lbl">{s.lbl}</div>
              </div>
            ))}
          </div>
        )}

        {/* ── Interventions du jour ── */}
        {show('inter_jour') && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>📋 Interventions en cours — aujourd'hui</span>
                {!loadingInterJour && interJour && (
                  <span style={{
                    background: interJour.total === 0 ? 'var(--bg3)' : 'rgba(59,130,246,0.15)',
                    color: interJour.total === 0 ? 'var(--text3)' : '#2563EB',
                    borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700,
                  }}>
                    {interJour.total} planifiée{interJour.total !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={loadInterJour}>↻</button>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/recherche?stage=Planifi%C3%A9e&date=' + (interJour?.date || ''))}>Voir dans recherche →</button>
              </div>
            </div>

            {loadingInterJour ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>Chargement…</div>
            ) : !interJour?.interventions?.length ? (
              <div className="empty"><div className="icon">✓</div><p>Aucune intervention planifiée aujourd'hui</p></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg3)' }}>
                      {['Heure', 'Technicien', 'Client', 'Réf commande', 'Adresse', 'Typo'].map(h => (
                        <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                      <th style={{ padding: '7px 12px', borderBottom: '1px solid var(--border)' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {interJour.interventions.map((inter, i) => (
                      <tr key={inter.id} style={{ background: i % 2 === 0 ? 'var(--bg2)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 700, color: 'var(--blue2)', whiteSpace: 'nowrap', fontFamily: 'var(--mono)' }}>
                          {inter.heure || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text)' }}>{inter.technicien || '—'}</div>
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--text)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {inter.client_final || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'var(--mono)', color: 'var(--blue2)', whiteSpace: 'nowrap', fontSize: 11 }}>
                          {inter.ref_commande || inter.ref_work_order || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[inter.adresse, inter.code_postal, inter.ville].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          {inter.typo ? (
                            <span style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1', borderRadius: 6, padding: '2px 7px', fontSize: 10, fontWeight: 700 }}>
                              {inter.typo}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '4px 12px', whiteSpace: 'nowrap' }}>
                          {inter.zoho_id && (
                            <a href={`https://crmplus.zoho.eu/valeatelecom/index.do/cxapp/crm/org20082707825/tab/Potentials/${inter.zoho_id}`}
                              target="_blank" rel="noreferrer"
                              style={{ display: 'inline-block', padding: '3px 8px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6, color: '#2563EB', fontSize: 11, fontWeight: 700, textDecoration: 'none' }}>
                              ↗ Zoho
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Compteur CRI */}
        {show('cri_counter') && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>⚠️ Suivi alertes</div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/suivi-cri')}>Voir détail →</button>
            </div>
            <div style={{ display: 'flex', gap: 14 }}>
              {[
                { lbl: 'CRI non clôturé', val: criStats?.cri ?? '—', color: '#f59e0b' },
                { lbl: 'CR non conforme', val: criStats?.cr_non_conforme ?? '—', color: '#ef4444' },
                { lbl: 'RDVNH', val: criStats?.rdvnh ?? '—', color: '#8b5cf6' },
                { lbl: 'Total alertes', val: criStats?.total ?? '—', color: 'var(--text)' },
              ].map((item, i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', padding: '12px 10px', background: 'var(--bg3)', borderRadius: 10 }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: item.color }}>{loading ? '…' : item.val}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{item.lbl}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

          {/* CR en attente */}
          {show('cr_pending') && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>CR en attente de validation</div>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/cr?filter=pending')}>Voir tous →</button>
              </div>
              {loading ? (
                <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Chargement…</div>
              ) : crPending.length === 0 ? (
                <div className="empty"><div className="icon">✓</div><p>Aucun CR en attente</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {crPending.slice(0, 5).map(cr => (
                    <div key={cr.id} onClick={() => navigate(`/cr/${cr.id}`)}
                      style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252d42'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--bg3)'}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'var(--mono)' }}>{cr.ref_commande || cr.dossier_id}</span>
                          <span className={`badge ${cr.resultat === 'CLEARED' ? 'badge-green' : cr.resultat === 'FAILED' ? 'badge-red' : 'badge-blue'}`}>
                            {cr.type_cr || cr.resultat || '—'}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text2)' }}>{cr.technicien_nom} · {cr.client_final || '—'}</div>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {cr.date_creation ? new Date(cr.date_creation).toLocaleDateString('fr-FR') : '—'}
                      </div>
                      <div style={{ color: 'var(--amber2)', fontSize: 16 }}>⏳</div>
                    </div>
                  ))}
                  {crPending.length > 5 && (
                    <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', paddingTop: 4 }}>
                      + {crPending.length - 5} autre(s) — <span style={{ color: 'var(--blue2)', cursor: 'pointer' }} onClick={() => navigate('/cr?filter=pending')}>voir tous</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Historique activité */}
          {show('activite_recente') && (
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Activité récente</div>
                <button className="btn btn-ghost btn-sm" onClick={() => load()}>↻ Actualiser</button>
              </div>
              {activity.length === 0 ? (
                <div className="empty"><div className="icon">📋</div><p>Aucune activité enregistrée</p></div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activity.slice(0, 8).map((a, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: ACTION_COLOR[a.action] || '#888', marginTop: 5, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                          <span style={{ color: ACTION_COLOR[a.action] }}>{ACTION_LABEL[a.action] || a.action}</span>
                          {' — '}
                          <span style={{ fontFamily: 'var(--mono)', color: 'var(--blue2)' }}>{a.ref_commande || a.cr_id}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                          {a.technicien} · {a.type_cr} {a.resultat && `· ${a.resultat}`}
                          {a.motif && <span style={{ color: '#ff6b6b' }}> · {a.motif}</span>}
                        </div>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {new Date(a.at).toLocaleDateString('fr-FR')}<br />
                        {new Date(a.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Import du jour */}
        {show('import_jour') && (
          <div className="card" style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>📥 Import commandes — aujourd'hui</div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/import')}>Importer →</button>
            </div>
            {loading || !importStats ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '10px 0' }}>{loading ? 'Chargement…' : 'Aucun import ce jour'}</div>
            ) : importStats.total === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 13 }}>Aucun import effectué aujourd'hui</div>
            ) : (
              <div style={{ display: 'flex', gap: 20 }}>
                <div style={{ textAlign: 'center', padding: '12px 20px', background: 'var(--bg3)', borderRadius: 10, flex: 1 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>{importStats.total}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Total importé</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px 20px', background: '#f0fdf4', borderRadius: 10, flex: 1 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--green2)' }}>{importStats.created}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Nouvelles interventions</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px 20px', background: '#eff6ff', borderRadius: 10, flex: 1 }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue2)' }}>{importStats.updated}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Mises à jour</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Techniciens */}
        {show('techniciens') && (
          <div className="card" style={{ marginTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Techniciens enregistrés</div>
              <button className="btn btn-ghost btn-sm" onClick={() => navigate('/techniciens')}>Gérer →</button>
            </div>
            {loading ? (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>Chargement…</div>
            ) : techs.filter(t => !t.disabled).length === 0 ? (
              <div className="empty"><div className="icon">👷</div><p>Aucun technicien actif</p></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                {techs.filter(t => !t.disabled).map(t => {
                  const ouvert = techOuvert === t.id;
                  const num = numeroInternational(t.telephone);
                  return (
                    <div key={t.id} style={{ background: 'var(--bg3)', borderRadius: 10,
                                             border: ouvert ? '1px solid var(--blue2)' : '1px solid transparent' }}>
                      <button type="button"
                        onClick={() => setTechOuvert(ouvert ? null : t.id)}
                        title={num ? 'Voir le numéro et joindre' : 'Aucun numéro enregistré'}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                                 padding: '10px 14px', background: 'none', border: 'none',
                                 borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                                 font: 'inherit', color: 'inherit' }}>
                        <div className="avatar" style={{ width: 36, height: 36, fontSize: 12, flexShrink: 0 }}>
                          {(t.prenom?.[0] || '?')}{(t.nom?.[0] || '')}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{t.prenom} {t.nom}</div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t.secteur || 'Secteur non défini'}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--text3)', flexShrink: 0 }}>
                          {ouvert ? '▴' : '▾'}
                        </span>
                      </button>

                      {ouvert && (
                        <div style={{ padding: '0 14px 12px', fontSize: 11.5, lineHeight: 1.6 }}>
                          <div style={{ color: 'var(--text2)', borderTop: '1px solid var(--border)', paddingTop: 9 }}>
                            {t.zoho_nom && (
                              <div><span style={{ color: 'var(--text3)' }}>Compte </span>{String(t.zoho_nom).replace('VALEA-', '')}</div>
                            )}
                            {t.entreprise && (
                              <div><span style={{ color: 'var(--text3)' }}>Société </span>{t.entreprise}</div>
                            )}
                            {/* Le numéro reste sélectionnable : le copier est
                                parfois la seule chose qu'on veut en faire. */}
                            <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', userSelect: 'text' }}>
                              {num ? numeroLisible(t.telephone) : 'Aucun numéro enregistré'}
                            </div>
                            {t.email && (
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <a href={'mailto:' + t.email} style={{ color: 'var(--blue2)' }}>{t.email}</a>
                              </div>
                            )}
                          </div>

                          {num ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
                              {/* wa.me ouvre WhatsApp Desktop s'il est installé,
                                  WhatsApp Web sinon. C'est le seul des trois qui
                                  ne dépend pas d'un logiciel associé au poste. */}
                              <a href={'https://wa.me/' + num} target="_blank" rel="noreferrer"
                                 title={'WhatsApp — ' + numeroLisible(t.telephone)}
                                 style={{ padding: '5px 11px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                                          textDecoration: 'none', color: '#059669',
                                          background: 'rgba(16,185,129,0.1)',
                                          border: '1px solid rgba(16,185,129,0.4)' }}>
                                WhatsApp
                              </a>
                              <a href={'tel:+' + num} title="Appeler depuis le logiciel de téléphonie du poste"
                                 style={{ padding: '5px 11px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                                          textDecoration: 'none', color: 'var(--blue2)',
                                          background: 'rgba(59,130,246,0.1)',
                                          border: '1px solid rgba(59,130,246,0.35)' }}>
                                Appeler
                              </a>
                              <a href={'sms:+' + num} title="SMS depuis le logiciel associé du poste"
                                 style={{ padding: '5px 11px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                                          textDecoration: 'none', color: 'var(--text2)',
                                          background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                                SMS
                              </a>
                            </div>
                          ) : (
                            <button type="button" onClick={() => navigate('/techniciens')}
                              style={{ marginTop: 8, padding: 0, background: 'none', border: 'none',
                                       color: 'var(--blue2)', fontSize: 11, fontWeight: 700,
                                       textDecoration: 'underline', cursor: 'pointer' }}>
                              Renseigner son numéro
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* ── ARC à traiter ─────────────────────────────────────────────── */}
        {show('arc_alertes') && (
          <AlerteWidget
            title="🚧 ARC à traiter"
            items={alertes?.arc || []}
            loading={loading || !alertes}
            emptyMsg="Aucun ARC en attente"
            counterMode="until"
            counterRedThreshold={3}
            hideIfPast={false}
          />
        )}

        {/* ── DOSTEC en attente ──────────────────────────────────────────── */}
        {show('dostec_alertes') && (
          <AlerteWidget
            title="⏳ DOSTEC en attente"
            items={alertes?.dostec || []}
            loading={loading || !alertes}
            emptyMsg="Aucun DOSTEC en attente"
            counterMode="since"
            counterRedThreshold={4}
            hideIfFuture={true}
          />
        )}

        {/* ── Nacelle nécessaire ─────────────────────────────────────────── */}
        {show('nacelle_alertes') && (
          <AlerteWidget
            title="🏗️ Nacelle nécessaire"
            items={alertes?.nacelle || []}
            loading={loading || !alertes}
            emptyMsg="Aucune nacelle prévue"
            counterMode="until"
            counterRedThreshold={3}
            hideIfPast={true}
          />
        )}

      </div>
    </>
  );
}

// ── Composant widget alerte Zoho ─────────────────────────────────────────────
function AlerteWidget({ title, items, loading, emptyMsg, counterMode, counterRedThreshold, hideIfPast = false, hideIfFuture = false }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const filtered = items.filter(item => {
    if (!item.date) return true; // pas de date → toujours affiché
    const d = new Date(item.date);
    d.setHours(0, 0, 0, 0);
    if (hideIfPast && d < today) return false;
    if (hideIfFuture && d >= today) return false;
    return true;
  });

  const getCounter = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const diffDays = Math.round((d - today) / 86400000);
    if (counterMode === 'until') {
      if (diffDays < 0) return null; // passé → pas de compteur
      return { days: diffDays, red: diffDays <= counterRedThreshold };
    } else { // since
      if (diffDays >= 0) return null; // futur → pas de compteur
      return { days: Math.abs(diffDays), red: Math.abs(diffDays) > counterRedThreshold };
    }
  };

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>
        {title}
        <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 400, color: 'var(--text3)' }}>
          ({loading ? '…' : filtered.length})
        </span>
      </div>
      {loading ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, padding: '10px 0' }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="empty"><div className="icon">✓</div><p>{emptyMsg}</p></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(item => {
            const counter = getCounter(item.date);
            return (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 13, fontFamily: 'var(--mono)', color: 'var(--blue2)' }}>{item.ref_commande || '—'}</span>
                    {item.typo && <span className="badge badge-blue" style={{ fontSize: 10 }}>{item.typo}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 2 }}>
                    {item.client && <span style={{ fontWeight: 600 }}>{item.client}</span>}
                    {item.technicien && <span style={{ color: 'var(--text3)' }}> · {item.technicien}</span>}
                  </div>
                  {item.adresse && (
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.adresse}</div>
                  )}
                  {(item.date || item.heure) && (
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                      {item.date && new Date(item.date).toLocaleDateString('fr-FR')}
                      {item.heure && ` à ${item.heure}`}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                  {counter !== null && (
                    <div style={{
                      padding: '4px 10px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      background: counter.red ? '#fef2f2' : '#f0fdf4',
                      color: counter.red ? '#ef4444' : 'var(--green2)',
                      whiteSpace: 'nowrap',
                    }}>
                      {counterMode === 'until' ? `J-${counter.days}` : `J+${counter.days}`}
                    </div>
                  )}
                  <Link
                    to={`/dossier/${item.id}`}
                    style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    onClick={e => e.stopPropagation()}
                  >
                    Fiche
                  </Link>
                  <a
                    href={`https://crm.zoho.eu/crm/tab/Potentials/${item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 11, color: 'var(--blue2)', textDecoration: 'none', whiteSpace: 'nowrap' }}
                    onClick={e => e.stopPropagation()}
                  >
                    Zoho →
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
